import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, formatMoney, formatMoney2, formatNumber, formatPct } from "../lib/api";
import { ArrowLeft, UploadCloud, Loader2, Sparkles, Download, Package, AlertTriangle, RotateCcw, TrendingUp, Star, Mail, X, Copy, Check, FileText, ShoppingCart } from "lucide-react";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";

export default function SupplierDetail() {
  const { id } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [catalog, setCatalog] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [studies, setStudies] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  // Analysis form
  const [studyId, setStudyId] = useState("");
  const [coverageDays, setCoverageDays] = useState(30);
  const [conditionLabs, setConditionLabs] = useState([]);
  const [labOptions, setLabOptions] = useState([]);
  const [labSearch, setLabSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: cat }, { data: studs }] = await Promise.all([
        api.get(`/suppliers/${id}`),
        api.get(`/suppliers/${id}/catalog`, { params: { limit: 50 } }),
        api.get("/studies"),
      ]);
      setSupplier(s);
      setCatalog(cat);
      setStudies(studs);
      if (studs[0]) setStudyId(studs[0].id);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!studyId) return setLabOptions([]);
    (async () => {
      try {
        const { data } = await api.get(`/studies/${studyId}`);
        setLabOptions(data.labs || []);
      } catch (e) {}
    })();
  }, [studyId]);

  const searchCatalog = async (q) => {
    setSearch(q);
    const { data } = await api.get(`/suppliers/${id}/catalog`, { params: { limit: 50, search: q || undefined } });
    setCatalog(data);
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setError("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/suppliers/${id}/catalog`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const runAnalysis = async () => {
    if (!studyId) return;
    setAnalyzing(true); setError("");
    try {
      const { data } = await api.post(`/suppliers/${id}/analyze`, {
        study_id: studyId, coverage_days: coverageDays, condition_labs: conditionLabs,
      });
      setAnalysis(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setAnalyzing(false); }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.post(`/suppliers/${id}/analyze/excel`, {
        study_id: studyId, coverage_days: coverageDays, condition_labs: conditionLabs,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${supplier.name}_analisis_proveedor.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleLab = (lab) => {
    setConditionLabs((cl) => cl.includes(lab) ? cl.filter(x => x !== lab) : [...cl, lab]);
  };

  // Sort hooks for the supplier tables — declared BEFORE early returns (hook rules)
  const catalogSort = useSortable(catalog.items || [], null, "desc");

  if (loading) return <div className="p-8 flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>;
  if (!supplier) return <div className="p-8 text-red-600">Proveedor no encontrado</div>;

  const filteredLabs = labOptions.filter((l) => !labSearch || l.toLowerCase().includes(labSearch.toLowerCase()));

  return (
    <div className="fade-in" data-testid="supplier-detail-page">
      <div className="px-8 pt-8 pb-4 border-b border-slate-200 bg-white sticky top-0 lg:top-[64px] z-20">
        <Link to="/suppliers" className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
          <ArrowLeft size={12} /> Volver a proveedores
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">{supplier.name}</h1>
            <p className="text-sm text-slate-600 mt-1">
              <span className="font-mono font-semibold">{formatNumber(supplier.products_count)}</span> productos en catálogo
              {supplier.last_uploaded && <> · actualizado {new Date(supplier.last_uploaded).toLocaleDateString("es-ES")}</>}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to={`/suppliers/${id}/order`} className="btn btn-primary btn-sm" data-testid="order-builder-link">
              <ShoppingCart size={14} /> Pedido directo + canje
            </Link>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onUpload} data-testid="catalog-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn btn-outline btn-sm" data-testid="upload-catalog-button">
              {uploading ? <><Loader2 size={14} className="animate-spin" /> Subiendo…</> : <><UploadCloud size={14} /> Subir / Reemplazar catálogo</>}
            </button>
          </div>
        </div>
        {error && <div className="mt-3 text-sm text-red-700 bg-red-50 px-3 py-2 rounded border border-red-200">{error}</div>}
      </div>

      <div className="p-8 space-y-6">
        {/* Analysis card */}
        <div className="card p-6" data-testid="analysis-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-slate-700" />
            <h3 className="font-display text-xl font-bold">Analizar contra estudio</h3>
          </div>
          <p className="text-sm text-slate-600 mb-5">
            Cruza el catálogo del proveedor con un estudio de farmacia para obtener:
            (1) productos que <b>faltan</b> en la farmacia, (2) oportunidades de <b>conversión</b> de genéricos
            desde laboratorios sin condición, y (3) <b>pedido de reposición</b> con los días de cobertura objetivo.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Estudio</label>
              <select className="input mt-1" value={studyId} onChange={(e) => setStudyId(e.target.value)} data-testid="analysis-study-select">
                {studies.length === 0 && <option value="">No tienes estudios</option>}
                {studies.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.period_days}d)</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Días de cobertura</label>
              <div className="flex items-center gap-3 mt-1">
                <input data-testid="coverage-days-input" type="range" min="7" max="180" value={coverageDays}
                       onChange={(e) => setCoverageDays(parseInt(e.target.value))} className="flex-1" />
                <input type="number" min="1" max="365" value={coverageDays}
                       onChange={(e) => setCoverageDays(parseInt(e.target.value) || 30)}
                       className="input" style={{ width: 80 }} />
                <span className="text-xs text-slate-500">días</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Labs con condición ({conditionLabs.length})</label>
              <input className="input mt-1" placeholder="Buscar laboratorio…" value={labSearch}
                     onChange={(e) => setLabSearch(e.target.value)} data-testid="lab-search-input" />
              <div className="mt-2 max-h-32 overflow-y-auto border border-slate-200 rounded p-2 bg-slate-50 text-xs space-y-1">
                {filteredLabs.length === 0 ? (
                  <div className="text-slate-400">Selecciona primero un estudio</div>
                ) : (
                  filteredLabs.slice(0, 200).map((lab) => (
                    <label key={lab} className="flex items-center gap-2 hover:bg-white px-1 py-0.5 rounded cursor-pointer">
                      <input type="checkbox" checked={conditionLabs.includes(lab)} onChange={() => toggleLab(lab)} data-testid={`lab-check-${lab}`} />
                      <span className="truncate">{lab}</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Estos labs se excluyen del análisis de conversión.</p>
            </div>
          </div>

          <div className="mt-5 flex gap-2">
            <button onClick={runAnalysis} disabled={!studyId || analyzing || supplier.products_count === 0}
                    className="btn btn-primary" data-testid="run-analysis-button">
              {analyzing ? <><Loader2 size={14} className="animate-spin" /> Analizando…</> : <><Sparkles size={14} /> Generar análisis</>}
            </button>
            {analysis && (
              <button onClick={downloadExcel} className="btn btn-outline" data-testid="download-analysis-excel">
                <Download size={14} /> Descargar Excel completo
              </button>
            )}
            {analysis && (
              <button onClick={() => setAnalysis(null)} className="btn btn-outline btn-sm">
                <RotateCcw size={14} /> Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Analysis results */}
        {analysis && <AnalysisResults analysis={analysis} />}

        {/* Margin analysis */}
        <MarginPanel supplier={supplier} studyId={studyId} studies={studies} setStudyId={setStudyId} />

        {/* Catalog preview */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl font-bold">Catálogo del proveedor</h3>
            <input data-testid="catalog-search" className="input" style={{ width: 280 }}
                   placeholder="Buscar CN o descripción…" value={search} onChange={(e) => searchCatalog(e.target.value)} />
          </div>
          {supplier.products_count === 0 ? (
            <div className="text-center py-8">
              <Package size={32} className="mx-auto text-slate-400" />
              <p className="text-slate-500 mt-2">Aún no hay catálogo. Sube un Excel/CSV con al menos CN y Descripción.</p>
              <p className="text-xs text-slate-400 mt-2">Columnas reconocidas: CN, Descripción, PVP, PMC, Laboratorio, Familia, Principio activo, Equivalentes (CNs separados por coma).</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex justify-end mb-2">
                <ExportButtons
                  filename={`catalogo-${supplier.name}`}
                  title={`Catálogo · ${supplier.name}`}
                  sheetName="Catalogo"
                  columns={[
                    { header: "CN", key: "cn" },
                    { header: "Descripción", key: "descripcion" },
                    { header: "Familia", key: "familia" },
                    { header: "Laboratorio", key: "laboratorio" },
                    { header: "PVP", key: "pvp", format: (v) => (v || 0).toFixed(2), align: "right" },
                    { header: "PMC", key: "pmc", format: (v) => (v || 0).toFixed(2), align: "right" },
                    { header: "Principio activo", key: "principio_activo" },
                    { header: "Equivalentes", key: "equivalentes" },
                  ]}
                  rows={catalogSort.sortedRows}
                />
              </div>
              <table className="dense" data-testid="catalog-table">
                <thead>
                  <tr>
                    <th><SortHeader label="CN" k="cn" sort={catalogSort} defaultDir="asc" /></th>
                    <th><SortHeader label="Descripción" k="descripcion" sort={catalogSort} defaultDir="asc" /></th>
                    <th><SortHeader label="Familia" k="familia" sort={catalogSort} defaultDir="asc" /></th>
                    <th><SortHeader label="Lab." k="laboratorio" sort={catalogSort} defaultDir="asc" /></th>
                    <th className="num"><SortHeader label="PVP" k="pvp" sort={catalogSort} num /></th>
                    <th className="num"><SortHeader label="PMC" k="pmc" sort={catalogSort} num /></th>
                    <th>Principio activo</th>
                    <th>Equivalentes</th>
                  </tr>
                </thead>
                <tbody>
                  {catalogSort.sortedRows.map((p, i) => (
                    <tr key={`c-${i}-${p.cn}`}>
                      <td className="font-mono">{p.cn}</td>
                      <td>{p.descripcion}</td>
                      <td className="text-xs">{p.familia || "—"}</td>
                      <td className="text-xs">{p.laboratorio || "—"}</td>
                      <td className="num">{p.pvp ? formatMoney2(p.pvp) : "—"}</td>
                      <td className="num">{p.pmc ? formatMoney2(p.pmc) : "—"}</td>
                      <td className="text-xs">{p.principio_activo || "—"}</td>
                      <td className="text-xs font-mono">{(p.equivalent_cns || []).join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {catalog.total > catalog.items.length && (
                <p className="text-xs text-slate-500 mt-2">Mostrando {catalog.items.length} de {formatNumber(catalog.total)}.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalysisResults({ analysis }) {
  const sm = analysis.summary;
  const [tab, setTab] = useState("faltantes");
  const faltantesSort = useSortable(analysis?.faltantes || [], null, "desc");
  const conversionSort = useSortable(analysis?.conversion || [], "tot_venta", "desc");
  const reposicionSort = useSortable(analysis?.reposicion || [], "pedido", "desc");
  return (
    <div className="card p-6 fade-in" data-testid="analysis-results">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={18} className="text-emerald-500" />
        <h3 className="font-display text-xl font-bold">Resultados</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className="border-l-4 border-red-500 pl-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Faltantes en farmacia</div>
          <div className="font-display text-2xl font-extrabold">{formatNumber(sm.faltantes_count)}</div>
          <div className="text-xs text-slate-500">productos del catálogo que NO tiene la farmacia</div>
        </div>
        <div className="border-l-4 border-amber-500 pl-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Conversión genéricos</div>
          <div className="font-display text-2xl font-extrabold">{formatNumber(sm.conversion_count)}</div>
          <div className="text-xs text-slate-500">{formatMoney(sm.conversion_potencial)} en ventas reconvertibles</div>
        </div>
        <div className="border-l-4 border-emerald-500 pl-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Pedido de reposición</div>
          <div className="font-display text-2xl font-extrabold">{formatNumber(sm.reposicion_uds)} uds</div>
          <div className="text-xs text-slate-500">{sm.reposicion_count} líneas · {formatMoney(sm.reposicion_coste)} estimado</div>
        </div>
      </div>

      <div className="tabs-list mb-4">
        <button onClick={() => setTab("faltantes")} className={"tab-trigger " + (tab === "faltantes" ? "active" : "")} data-testid="tab-faltantes">
          Faltantes ({sm.faltantes_count})
        </button>
        <button onClick={() => setTab("conversion")} className={"tab-trigger " + (tab === "conversion" ? "active" : "")} data-testid="tab-conversion">
          Conversión ({sm.conversion_count})
        </button>
        <button onClick={() => setTab("reposicion")} className={"tab-trigger " + (tab === "reposicion" ? "active" : "")} data-testid="tab-reposicion">
          Pedido reposición ({sm.reposicion_count})
        </button>
      </div>

      <div className="overflow-x-auto">
        {tab === "faltantes" && (
          <>
          <div className="flex justify-end mb-2">
            <ExportButtons
              filename="faltantes"
              title="Faltantes en farmacia"
              sheetName="Faltantes"
              columns={[
                { header: "CN", key: "cn" },
                { header: "Descripción", key: "descripcion" },
                { header: "Laboratorio", key: "laboratorio" },
                { header: "PVP", key: "pvp", format: (v) => (v || 0).toFixed(2), align: "right" },
                { header: "PMC", key: "pmc", format: (v) => (v || 0).toFixed(2), align: "right" },
              ]}
              rows={faltantesSort.sortedRows}
            />
          </div>
          <table className="dense" data-testid="faltantes-table">
            <thead>
              <tr>
                <th><SortHeader label="CN" k="cn" sort={faltantesSort} defaultDir="asc" /></th>
                <th><SortHeader label="Descripción" k="descripcion" sort={faltantesSort} defaultDir="asc" /></th>
                <th><SortHeader label="Lab." k="laboratorio" sort={faltantesSort} defaultDir="asc" /></th>
                <th className="num"><SortHeader label="PVP" k="pvp" sort={faltantesSort} num /></th>
                <th className="num"><SortHeader label="PMC" k="pmc" sort={faltantesSort} num /></th>
              </tr>
            </thead>
            <tbody>
              {faltantesSort.sortedRows.slice(0, 100).map((p, i) => (
                <tr key={`fa-${i}-${p.cn}`}>
                  <td className="font-mono">{p.cn}</td><td>{p.descripcion}</td>
                  <td className="text-xs">{p.laboratorio}</td>
                  <td className="num">{formatMoney2(p.pvp)}</td><td className="num">{formatMoney2(p.pmc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
        {tab === "conversion" && (
          analysis.conversion.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <AlertTriangle size={28} className="mx-auto text-slate-300" />
              <p className="mt-2">Sin oportunidades detectadas. Para mejorar el match añade columnas <span className="font-mono font-semibold">Equivalentes</span> (CNs competidor separados por coma) o <span className="font-mono font-semibold">Principio activo</span> en tu catálogo.</p>
            </div>
          ) : (
            <>
            <div className="flex justify-end mb-2">
              <ExportButtons
                filename="conversiones"
                title="Conversiones a genéricos"
                sheetName="Conversion"
                orientation="landscape"
                columns={[
                  { header: "CN actual", key: "cn_competidor" },
                  { header: "Descripción actual", key: "descripcion_competidor" },
                  { header: "Lab. actual", key: "laboratorio_actual" },
                  { header: "Uds vendidas", key: "uds_vendidas", align: "right" },
                  { header: "Ventas (€)", key: "tot_venta", format: (v) => (v || 0).toFixed(2), align: "right" },
                  { header: "CN propuesto", key: "cn_supplier" },
                  { header: "Descripción propuesta", key: "descripcion_supplier" },
                  { header: "PVP propuesto", key: "pvp_supplier", format: (v) => (v || 0).toFixed(2), align: "right" },
                  { header: "Match", key: "match_type" },
                ]}
                rows={conversionSort.sortedRows}
              />
            </div>
            <table className="dense" data-testid="conversion-table">
              <thead>
                <tr>
                  <th><SortHeader label="CN actual" k="cn_competidor" sort={conversionSort} defaultDir="asc" /></th>
                  <th><SortHeader label="Descripción actual" k="descripcion_competidor" sort={conversionSort} defaultDir="asc" /></th>
                  <th><SortHeader label="Lab. actual" k="laboratorio_actual" sort={conversionSort} defaultDir="asc" /></th>
                  <th className="num"><SortHeader label="Uds vendidas" k="uds_vendidas" sort={conversionSort} num /></th>
                  <th className="num"><SortHeader label="Ventas" k="tot_venta" sort={conversionSort} num /></th>
                  <th><SortHeader label="CN propuesto" k="cn_supplier" sort={conversionSort} defaultDir="asc" /></th>
                  <th><SortHeader label="Descripción propuesta" k="descripcion_supplier" sort={conversionSort} defaultDir="asc" /></th>
                  <th className="num"><SortHeader label="PVP prop." k="pvp_supplier" sort={conversionSort} num /></th>
                  <th><SortHeader label="Match" k="match_type" sort={conversionSort} defaultDir="asc" /></th>
                </tr>
              </thead>
              <tbody>
                {conversionSort.sortedRows.slice(0, 100).map((p, i) => (
                  <tr key={`cv-${i}-${p.cn_competidor}-${p.cn_supplier}`}>
                    <td className="font-mono">{p.cn_competidor}</td><td>{p.descripcion_competidor}</td>
                    <td className="text-xs">{p.laboratorio_actual}</td>
                    <td className="num">{formatNumber(p.uds_vendidas)}</td>
                    <td className="num">{formatMoney2(p.tot_venta)}</td>
                    <td className="font-mono">{p.cn_supplier}</td><td>{p.descripcion_supplier}</td>
                    <td className="num">{formatMoney2(p.pvp_supplier)}</td>
                    <td><span className="abcd-pill abcd-A">{p.match_type}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )
        )}
        {tab === "reposicion" && (
          <>
          <div className="flex justify-end mb-2">
            <ExportButtons
              filename="propuesta-pedido"
              title="Propuesta de reposición"
              sheetName="Reposicion"
              orientation="landscape"
              columns={[
                { header: "CN", key: "cn" },
                { header: "Descripción", key: "descripcion" },
                { header: "ABCD", key: "abcd" },
                { header: "Stock", key: "stock_actual", align: "right" },
                { header: "Uds vendidas", key: "uds_vendidas_periodo", align: "right" },
                { header: "Venta diaria", key: "venta_diaria", format: (v) => (v || 0).toFixed(2), align: "right" },
                { header: "Objetivo stock", key: "objetivo_stock", align: "right" },
                { header: "Pedido", key: "pedido", align: "right" },
                { header: "PMC", key: "pmc", format: (v) => (v || 0).toFixed(2), align: "right" },
                { header: "Coste est. (€)", key: "coste_estimado", format: (v) => (v || 0).toFixed(2), align: "right" },
              ]}
              rows={reposicionSort.sortedRows}
            />
          </div>
          <table className="dense" data-testid="reposicion-table">
            <thead>
              <tr>
                <th><SortHeader label="CN" k="cn" sort={reposicionSort} defaultDir="asc" /></th>
                <th><SortHeader label="Descripción" k="descripcion" sort={reposicionSort} defaultDir="asc" /></th>
                <th><SortHeader label="ABCD" k="abcd" sort={reposicionSort} defaultDir="asc" /></th>
                <th className="num"><SortHeader label="Stock" k="stock_actual" sort={reposicionSort} num /></th>
                <th className="num"><SortHeader label="Uds vendidas" k="uds_vendidas_periodo" sort={reposicionSort} num /></th>
                <th className="num"><SortHeader label="Venta diaria" k="venta_diaria" sort={reposicionSort} num /></th>
                <th className="num"><SortHeader label="Objetivo" k="objetivo_stock" sort={reposicionSort} num /></th>
                <th className="num"><SortHeader label="Pedido" k="pedido" sort={reposicionSort} num /></th>
                <th className="num"><SortHeader label="PMC" k="pmc" sort={reposicionSort} num /></th>
                <th className="num"><SortHeader label="Coste est." k="coste_estimado" sort={reposicionSort} num /></th>
              </tr>
            </thead>
            <tbody>
              {reposicionSort.sortedRows.slice(0, 100).map((p, i) => (
                <tr key={`rp-${i}-${p.cn}`}>
                  <td className="font-mono">{p.cn}</td><td>{p.descripcion}</td>
                  <td><span className={`abcd-pill abcd-${p.abcd}`}>{p.abcd}</span></td>
                  <td className="num">{formatNumber(p.stock_actual)}</td>
                  <td className="num">{formatNumber(p.uds_vendidas_periodo)}</td>
                  <td className="num">{p.venta_diaria.toFixed(2)}</td>
                  <td className="num">{formatNumber(p.objetivo_stock)}</td>
                  <td className="num font-bold">{formatNumber(p.pedido)}</td>
                  <td className="num">{formatMoney2(p.pmc)}</td>
                  <td className="num font-bold">{formatMoney2(p.coste_estimado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>
    </div>
  );
}

function MarginPanel({ supplier, studyId, studies, setStudyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [labSort, setLabSort] = useState("margin");
  const [showAllLabs, setShowAllLabs] = useState(false);
  const [letterLab, setLetterLab] = useState(null);
  const [visitPlanLab, setVisitPlanLab] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const marginProductsSort = useSortable(data?.supplier_products || [], "delta", "desc");

  const downloadVisitPlan = async (lab) => {
    setPlanLoading(true);
    try {
      const res = await api.post(`/suppliers/${supplier.id}/visit-plan/pdf`, {
        study_id: studyId, target_lab: lab, tone: "profesional"
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `plan_visita_${lab}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Error al generar plan: " + (e.response?.data?.detail || e.message));
    } finally {
      setPlanLoading(false);
      setVisitPlanLab(null);
    }
  };

  // Auto-trigger download when visitPlanLab is set
  React.useEffect(() => {
    if (visitPlanLab && !planLoading) downloadVisitPlan(visitPlanLab);
    // eslint-disable-next-line
  }, [visitPlanLab]);

  const run = async () => {
    if (!studyId) return;
    setLoading(true); setError("");
    try {
      const { data: d } = await api.post(`/suppliers/${supplier.id}/margin-analysis`, { study_id: studyId });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const downloadExcel = async () => {
    try {
      const res = await api.post(`/suppliers/${supplier.id}/margin-analysis/excel`,
        { study_id: studyId }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${supplier.name}_margen.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
  };

  const sortedLabs = React.useMemo(() => {
    if (!data?.labs_margin) return [];
    const arr = [...data.labs_margin];
    if (labSort === "margin") arr.sort((a, b) => b.margin - a.margin);
    else if (labSort === "margin_pct_desc") arr.sort((a, b) => b.margin_pct - a.margin_pct);
    else arr.sort((a, b) => a.margin_pct - b.margin_pct);
    return arr;
  }, [data, labSort]);

  // Header click sort — overrides the dropdown when the user clicks any column
  const labSortHook = useSortable(sortedLabs, null, "desc");

  return (
    <div className="card p-6" data-testid="margin-panel">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={18} className="text-emerald-600" />
        <h3 className="font-display text-xl font-bold">Margen real por laboratorio</h3>
      </div>
      <p className="text-sm text-slate-600 mb-5">
        Cruza el <b>PMC del catálogo del proveedor</b> con las ventas reales de la farmacia.
        Detecta el margen € que aporta cada laboratorio y los que están sub-rentabilizados
        para tener argumentos numéricos en la negociación.
      </p>

      <div className="flex gap-2 items-end flex-wrap mb-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Estudio</label>
          <select className="input mt-1" value={studyId} onChange={(e) => setStudyId(e.target.value)} data-testid="margin-study-select" style={{ minWidth: 260 }}>
            {studies.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.period_days}d)</option>)}
          </select>
        </div>
        <button onClick={run} disabled={!studyId || loading || supplier.products_count === 0}
                className="btn btn-primary" data-testid="run-margin-button">
          {loading ? <><Loader2 size={14} className="animate-spin" /> Calculando…</> : <><TrendingUp size={14} /> Calcular margen</>}
        </button>
        {data && (
          <button onClick={downloadExcel} className="btn btn-outline" data-testid="download-margin-excel">
            <Download size={14} /> Descargar Excel
          </button>
        )}
      </div>

      {error && <div className="mt-2 mb-4 text-sm text-red-700 bg-red-50 px-3 py-2 rounded border border-red-200">{error}</div>}

      {data && (
        <div className="space-y-6 fade-in">
          {/* Supplier KPIs */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
              Productos de <b>{data.supplier.name}</b> en la farmacia
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="border-l-4 border-slate-900 pl-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Productos</div>
                <div className="font-display text-xl font-extrabold">{formatNumber(data.supplier_summary.products_in_pharmacy)}</div>
                <div className="text-[11px] text-slate-500">{formatNumber(data.supplier_summary.units_sold)} uds</div>
              </div>
              <div className="border-l-4 border-blue-500 pl-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ventas</div>
                <div className="font-display text-xl font-extrabold">{formatMoney(data.supplier_summary.sales)}</div>
              </div>
              <div className="border-l-4 border-amber-500 pl-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Margen farmacia</div>
                <div className="font-display text-xl font-extrabold">{formatMoney(data.supplier_summary.margin_farmacia)}</div>
                <div className="text-[11px] text-slate-500">{formatPct(data.supplier_summary.margin_pct_farmacia)}</div>
              </div>
              <div className="border-l-4 border-emerald-500 pl-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Margen real (catálogo)</div>
                <div className="font-display text-xl font-extrabold">{formatMoney(data.supplier_summary.margin_catalogo)}</div>
                <div className="text-[11px] text-slate-500">{formatPct(data.supplier_summary.margin_pct_catalogo)}</div>
              </div>
              <div className={"border-l-4 pl-3 " + (data.supplier_summary.delta > 0 ? "border-red-500" : "border-slate-300")}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Delta a renegociar</div>
                <div className={"font-display text-xl font-extrabold " + (data.supplier_summary.delta > 0 ? "text-red-600" : "")}>
                  {formatMoney(data.supplier_summary.delta)}
                </div>
                <div className="text-[11px] text-slate-500">
                  {data.supplier_summary.delta > 0 ? "PMC catálogo < PMC farmacia" : "Sin desviación"}
                </div>
              </div>
            </div>
          </div>

          {/* Per-product detail */}
          {data.supplier_products.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Detalle por producto (ordenado por mayor delta)</div>
                <div className="text-xs text-slate-500">{data.supplier_products.length} productos</div>
              </div>
              <div className="overflow-x-auto max-h-96">
                <div className="flex justify-end mb-2">
                  <ExportButtons
                    filename="margenes-productos"
                    title="Productos · margen real"
                    sheetName="Margen-productos"
                    orientation="landscape"
                    columns={[
                      { header: "CN", key: "cn" },
                      { header: "Descripción", key: "descripcion" },
                      { header: "ABCD", key: "abcd" },
                      { header: "Uds", key: "uds_vendidas", align: "right" },
                      { header: "PVP", key: "pvp", format: (v) => (v || 0).toFixed(2), align: "right" },
                      { header: "PMC Farm.", key: "pmc_farmacia", format: (v) => (v || 0).toFixed(2), align: "right" },
                      { header: "PMC Catál.", key: "pmc_catalogo", format: (v) => (v || 0).toFixed(2), align: "right" },
                      { header: "Ventas (€)", key: "sales", format: (v) => (v || 0).toFixed(2), align: "right" },
                      { header: "Mg Farm. (€)", key: "margin_farmacia", format: (v) => (v || 0).toFixed(2), align: "right" },
                      { header: "Mg Real (€)", key: "margin_catalogo", format: (v) => (v || 0).toFixed(2), align: "right" },
                      { header: "Delta (€)", key: "delta", format: (v) => (v || 0).toFixed(2), align: "right" },
                    ]}
                    rows={marginProductsSort.sortedRows}
                  />
                </div>
                <table className="dense" data-testid="margin-products-table">
                  <thead>
                    <tr>
                      <th><SortHeader label="CN" k="cn" sort={marginProductsSort} defaultDir="asc" /></th>
                      <th><SortHeader label="Descripción" k="descripcion" sort={marginProductsSort} defaultDir="asc" /></th>
                      <th><SortHeader label="ABCD" k="abcd" sort={marginProductsSort} defaultDir="asc" /></th>
                      <th className="num"><SortHeader label="Uds" k="uds_vendidas" sort={marginProductsSort} num /></th>
                      <th className="num"><SortHeader label="PVP" k="pvp" sort={marginProductsSort} num /></th>
                      <th className="num"><SortHeader label="PMC Farm." k="pmc_farmacia" sort={marginProductsSort} num /></th>
                      <th className="num"><SortHeader label="PMC Catál." k="pmc_catalogo" sort={marginProductsSort} num /></th>
                      <th className="num"><SortHeader label="Ventas" k="sales" sort={marginProductsSort} num /></th>
                      <th className="num"><SortHeader label="Mg Farm." k="margin_farmacia" sort={marginProductsSort} num /></th>
                      <th className="num"><SortHeader label="Mg Real" k="margin_catalogo" sort={marginProductsSort} num /></th>
                      <th className="num"><SortHeader label="Delta €" k="delta" sort={marginProductsSort} num /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {marginProductsSort.sortedRows.slice(0, 100).map((p, i) => (
                      <tr key={`mp-${i}-${p.cn}`}>
                        <td className="font-mono">{p.cn}</td>
                        <td>{p.descripcion}</td>
                        <td><span className={`abcd-pill abcd-${p.abcd}`}>{p.abcd}</span></td>
                        <td className="num">{formatNumber(p.uds_vendidas)}</td>
                        <td className="num">{formatMoney2(p.pvp)}</td>
                        <td className="num">{formatMoney2(p.pmc_farmacia)}</td>
                        <td className="num">{formatMoney2(p.pmc_catalogo)}</td>
                        <td className="num">{formatMoney2(p.sales)}</td>
                        <td className="num">{formatMoney2(p.margin_farmacia)}</td>
                        <td className="num">{formatMoney2(p.margin_catalogo)}</td>
                        <td className={"num font-bold " + (p.delta > 0 ? "text-emerald-600" : p.delta < 0 ? "text-red-600" : "")}>
                          {formatMoney2(p.delta)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Lab ranking */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Ranking de laboratorios de la farmacia</div>
                <div className="text-sm text-slate-600 mt-0.5">
                  Total margen farmacia: <b>{formatMoney(data.pharmacy_totals.margin)}</b> · {formatPct(data.pharmacy_totals.margin_pct)} sobre {formatMoney(data.pharmacy_totals.sales)}
                </div>
              </div>
              <select className="input" style={{ width: 240 }} value={labSort} onChange={(e) => setLabSort(e.target.value)} data-testid="lab-sort-select">
                <option value="margin">Mayor margen € (top labs)</option>
                <option value="margin_pct_desc">Mayor margen % (más rentables)</option>
                <option value="margin_pct_asc">Menor margen % (sub-rentabilizados ⚠️)</option>
              </select>
            </div>
            <div className="overflow-x-auto max-h-96">
              <div className="flex justify-end mb-2">
                <ExportButtons
                  filename="ranking-laboratorios-margen"
                  title="Ranking laboratorios · margen real"
                  sheetName="Lab-ranking"
                  orientation="landscape"
                  columns={[
                    { header: "Laboratorio", key: "laboratorio" },
                    { header: "Productos", key: "products", align: "right" },
                    { header: "Uds", key: "uds", align: "right" },
                    { header: "Ventas (€)", key: "sales", format: (v) => (v || 0).toFixed(2), align: "right" },
                    { header: "Coste (€)", key: "cost", format: (v) => (v || 0).toFixed(2), align: "right" },
                    { header: "Margen (€)", key: "margin", format: (v) => (v || 0).toFixed(2), align: "right" },
                    { header: "Margen %", key: "margin_pct", format: (v) => ((v || 0) * 100).toFixed(1) + "%", align: "right" },
                  ]}
                  rows={labSortHook.sortedRows}
                />
              </div>
              <table className="dense" data-testid="lab-ranking-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th><SortHeader label="Laboratorio" k="laboratorio" sort={labSortHook} defaultDir="asc" /></th>
                    <th className="num"><SortHeader label="Productos" k="products" sort={labSortHook} num /></th>
                    <th className="num"><SortHeader label="Uds" k="uds" sort={labSortHook} num /></th>
                    <th className="num"><SortHeader label="Ventas" k="sales" sort={labSortHook} num /></th>
                    <th className="num"><SortHeader label="Coste" k="cost" sort={labSortHook} num /></th>
                    <th className="num"><SortHeader label="Margen €" k="margin" sort={labSortHook} num /></th>
                    <th className="num"><SortHeader label="Margen %" k="margin_pct" sort={labSortHook} num /></th>
                    <th>IA</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllLabs ? labSortHook.sortedRows : labSortHook.sortedRows.slice(0, 20)).map((l, i) => (
                    <tr key={`lab-${i}-${l.laboratorio}`} className={l.is_supplier ? "bg-amber-50" : ""}>
                      <td className="font-mono text-xs">{i + 1}</td>
                      <td className="font-semibold">
                        {l.is_supplier && <Star size={12} className="inline text-amber-500 mr-1" />}
                        {l.laboratorio}
                      </td>
                      <td className="num">{formatNumber(l.products)}</td>
                      <td className="num">{formatNumber(l.uds)}</td>
                      <td className="num">{formatMoney(l.sales)}</td>
                      <td className="num">{formatMoney(l.cost)}</td>
                      <td className={"num font-bold " + (l.margin < 0 ? "text-red-600" : "")}>{formatMoney(l.margin)}</td>
                      <td className={"num " + (l.margin_pct < 0.10 ? "text-red-600 font-bold" : l.margin_pct > 0.35 ? "text-emerald-600 font-bold" : "")}>
                        {formatPct(l.margin_pct)}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setLetterLab(l.laboratorio)}
                            className="text-xs text-slate-700 hover:text-slate-900 hover:underline inline-flex items-center gap-1"
                            data-testid={`negotiate-${l.laboratorio}`}
                            title="Generar email de negociación con IA"
                          >
                            <Mail size={12} /> Email
                          </button>
                          <button
                            onClick={() => setVisitPlanLab(l.laboratorio)}
                            className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline inline-flex items-center gap-1 ml-2"
                            data-testid={`visit-plan-${l.laboratorio}`}
                            title="Generar Plan de visita comercial (PDF)"
                          >
                            <FileText size={12} /> Plan PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sortedLabs.length > 20 && (
              <button onClick={() => setShowAllLabs(!showAllLabs)} className="text-sm font-semibold text-slate-700 hover:underline mt-2" data-testid="toggle-all-labs">
                {showAllLabs ? "Ver solo top 20" : `Ver los ${sortedLabs.length} laboratorios →`}
              </button>
            )}
          </div>
        </div>
      )}

      {!data && !loading && (
        <div className="text-sm text-slate-500 mt-4 italic">
          Pulsa "Calcular margen" para generar el cruce. Necesitas un catálogo cargado con columna PMC para detectar deltas.
        </div>
      )}

      {letterLab && (
        <NegotiationDialog
          supplierId={supplier.id}
          studyId={studyId}
          targetLab={letterLab}
          onClose={() => setLetterLab(null)}
        />
      )}

      {planLoading && (
        <div className="fixed bottom-6 right-6 bg-slate-900 text-white px-4 py-3 rounded-lg shadow-2xl flex items-center gap-2 z-50 fade-in" data-testid="visit-plan-loading">
          <Loader2 className="animate-spin" size={16} />
          <span className="text-sm">Generando plan de visita con IA…</span>
        </div>
      )}
    </div>
  );
}

function NegotiationDialog({ supplierId, studyId, targetLab, onClose }) {
  const [tone, setTone] = useState("profesional");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setLoading(true); setError("");
    try {
      const { data: d } = await api.post(`/suppliers/${supplierId}/negotiation-letter`, {
        study_id: studyId, target_lab: targetLab, tone,
      });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  React.useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const copyToClipboard = () => {
    if (!data?.letter) return;
    navigator.clipboard.writeText(data.letter);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 z-[60] bg-white flex flex-col fade-in" data-testid="negotiation-dialog">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Carta de negociación · IA</div>
            <h3 className="font-display text-xl font-bold mt-1">{targetLab}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100" data-testid="negotiation-close"><X size={18} /></button>
        </div>

        <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Tono</label>
          <select value={tone} onChange={(e) => setTone(e.target.value)} className="input" style={{ width: 160 }} data-testid="negotiation-tone">
            <option value="profesional">Profesional</option>
            <option value="cordial">Cordial</option>
            <option value="directo">Directo</option>
          </select>
          <button onClick={generate} disabled={loading} className="btn btn-outline btn-sm" data-testid="regenerate-letter">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Regenerar
          </button>
          {data && (
            <button onClick={copyToClipboard} className="btn btn-primary btn-sm ml-auto" data-testid="copy-letter">
              {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar al portapapeles</>}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Generando carta con IA…</div>
          ) : error ? (
            <div className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded border border-red-200">{error}</div>
          ) : data && (
            <>
              <div className="grid grid-cols-4 gap-3 mb-5 text-sm">
                <div><div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Productos</div><div className="font-display text-lg font-bold">{formatNumber(data.stats.products)}</div></div>
                <div><div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Ventas</div><div className="font-display text-lg font-bold">{formatMoney(data.stats.sales)}</div></div>
                <div><div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Margen</div><div className="font-display text-lg font-bold">{formatMoney(data.stats.margin)}</div></div>
                <div><div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Margen %</div><div className="font-display text-lg font-bold">{formatPct(data.stats.margin_pct)}</div></div>
              </div>
              <pre className="whitespace-pre-wrap text-sm font-sans bg-slate-50 border border-slate-200 rounded p-4 leading-relaxed" data-testid="negotiation-letter">{data.letter}</pre>
            </>
          )}
        </div>
      </div>
    </>
  );
}

